// src/services/api.js (Updated with JWT token management)
import axios from 'axios';
import { dedupedRequest, dedupedConfigRequest } from '../utils/requestDeduplication';
import { 
  getAccessToken, 
  isTokenExpired, 
  needsTokenRefresh, 
  refreshTokens, 
  clearTokens,
  storeTokens 
} from '../utils/tokenManager';

// Use environment variables for the API base URL, defaulting for local development
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.felicitafragrances.com/api';

// Simple language helper - Turkish by default, user can change manually
const getLanguageHeader = () => {
  return localStorage.getItem('i18nextLng') || 'tr';
};

// Create a reusable Axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Track ongoing refresh to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Enhanced request interceptor with automatic token refresh
apiClient.interceptors.request.use(async config => {
  const language = getLanguageHeader();
  config.headers['Accept-Language'] = language;
  
  // Get token from token manager
  const token = getAccessToken();
  
  if (token) {
    // Check if token is expired or needs refresh
    if (isTokenExpired(token)) {
      // Token is expired, clear it and don't add to request
      clearTokens();
    } else if (needsTokenRefresh() && !isRefreshing) {
      // Token needs refresh but still valid, attempt refresh
      try {
        isRefreshing = true;
        await refreshTokens(async (refreshToken) => {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
          return response.data;
        });
        
        // Get the new token after refresh
        const newToken = getAccessToken();
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`;
        }
        
        processQueue(null, newToken);
      } catch (error) {
        console.error('Token refresh failed in request interceptor:', error);
        processQueue(error, null);
        clearTokens();
      } finally {
        isRefreshing = false;
      }
    } else if (isRefreshing) {
      // If refresh is in progress, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    } else {
      // Token is valid, add it to the request
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  return config;
}, error => {
  return Promise.reject(error);
});

// Enhanced response interceptor with retry logic
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized responses
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        await refreshTokens(async (refreshToken) => {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
          return response.data;
        });
        
        const newToken = getAccessToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed in response interceptor:', refreshError);
        processQueue(refreshError, null);
        
        // Clear tokens and redirect to login
        clearTokens();
        
        // Only redirect if not already on auth pages
        if (window.location.pathname !== '/login' && 
            window.location.pathname !== '/register' &&
            window.location.pathname !== '/forgot-password' &&
            window.location.pathname !== '/reset-password') {
          // Store current location for redirect after login
          localStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
          window.location.href = '/login';
        }
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// --- Product API Endpoints ---
export const productApi = {
  getProducts: async (page = 0, size = 16, sort = 'createdAt,desc') => {
    const sortParam = Array.isArray(sort) ? sort.join('&sort=') : sort;
    const cacheKey = `products-${page}-${size}-${sortParam}`;
    
    return dedupedRequest(
      cacheKey,
      async () => {
        try {
          const response = await apiClient.get('/products', {
            params: { page, size, sort: sortParam }
          });
          return response.data;
        } catch (error) {

          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 30000 } // Cache for 30 seconds
    );
  },

  getProductById: async (id) => {
    try {
      const response = await apiClient.get(`/products/${id}`);
      return response.data;
    } catch (error) {

      throw error.response ? error.response.data : error;
    }
  },

  getProductsByCategory: async (categoryId, page = 0, size = 16, sort = 'createdAt,desc', search = '') => {
    const sortParam = Array.isArray(sort) ? sort.join('&sort=') : sort;
    const searchParam = search ? encodeURIComponent(search) : '';
    const cacheKey = `products-category-${categoryId}-${page}-${size}-${sortParam}-${searchParam}`;
    
    return dedupedRequest(
      cacheKey,
      async () => {
        try {
          const params = { page, size, sort: sortParam };
          if (search) {
            params.search = search;
          }
          const response = await apiClient.get(`/products/category/${categoryId}`, {
            params
          });
          return response.data;
        } catch (error) {

          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 30000 } // Cache for 30 seconds
    );
  },

  searchProducts: async (query, page = 0, size = 16, sort = 'createdAt,desc') => {
    const sortParam = Array.isArray(sort) ? sort.join('&sort=') : sort;
    const cacheKey = `products-search-${encodeURIComponent(query)}-${page}-${size}-${sortParam}`;
    
    return dedupedRequest(
      cacheKey,
      async () => {
        try {
          const response = await apiClient.get('/products/search', {
            params: { q: query, page, size, sort: sortParam }
          });
          return response.data;
        } catch (error) {

          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 30000 } // Cache for 30 seconds
    );
  },

  getCategoryFilters: async (categoryId) => {
    const cacheKey = `category-filters-${categoryId}`;
    
    return dedupedRequest(
      cacheKey,
      async () => {
        try {
          const response = await apiClient.get(`/products/category/${categoryId}/filters`);
          return response.data;
        } catch (error) {
          console.error(`Error fetching filters for category ${categoryId}:`, error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 3600000 } // Cache for 1 hour as filter metadata changes infrequently
    );
  },

  getFilteredProducts: async (categoryId, filters, page = 0, size = 20, sort = 'createdAt,desc', search = '') => {
    const sortParam = Array.isArray(sort) ? sort.join('&sort=') : sort;
    const filterKey = JSON.stringify(filters);
    const searchParam = search ? encodeURIComponent(search) : '';
    const cacheKey = `filtered-products-${categoryId}-${filterKey}-${page}-${size}-${sortParam}-${searchParam}`;
    
    return dedupedRequest(
      cacheKey,
      async () => {
        try {
          const params = { page, size, sort: sortParam };
          if (search) {
            params.search = search;
          }
          const response = await apiClient.post(
            `/products/category/${categoryId}/filtered`,
            { filters },
            {
              params
            }
          );
          return response.data;
        } catch (error) {
          console.error(`Error fetching filtered products for category ${categoryId}:`, error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 30000 } // Cache for 30 seconds
    );
  },
};

// --- Category API Endpoints ---
export const categoryApi = {
  getAllCategories: async () => {
    return dedupedConfigRequest(
      'all-categories',
      async () => {
        try {
          const response = await apiClient.get('/categories');
          return response.data;
        } catch (error) {

          throw error.response ? error.response.data : error;
        }
      }
    );
  },

  getCategoryHierarchy: async () => {
    return dedupedConfigRequest(
      'category-hierarchy',
      async () => {
        try {
          const response = await apiClient.get('/categories/hierarchy');
          return response.data;
        } catch (error) {

          throw error.response ? error.response.data : error;
        }
      }
    );
  },
};

// --- Order & Checkout API Endpoints ---
export const orderApi = {
  /**
   * Initiates the 3DS payment flow.
   * On success, returns { success: true, threeDSHtmlContent: "...", orderNumber: "..." }
   * On failure, throws an error with a descriptive message.
   */
  checkout: async (checkoutData) => {
    try {
      const lang = getLanguageHeader();
      const payload = { ...checkoutData, languagePreference: lang };

      if (process.env.NODE_ENV === 'development') {

      }

      // Iyzico SDK may take up to ~140s; keep checkout above that so the client
      // does not abort mid-init (which surfaces as Network Error / connection reset).
      const response = await apiClient.post('/checkout', payload, { timeout: 150000 });
      return response.data; // This will be the IyzicoPaymentResponse object
    } catch (error) {
      console.error('Error processing checkout:', error.response ? error.response.data : error.message);
      if (error.response?.data) {
        throw error.response.data;
      }
      if (error.code === 'ECONNABORTED' || error.message?.toLowerCase?.().includes('timeout')) {
        throw new Error('CHECKOUT_TIMEOUT');
      }
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        throw new Error('CHECKOUT_NETWORK');
      }
      throw new Error('Checkout failed due to a network or server error.');
    }
  },

  getOrderByNumber: async (orderNumber) => {
    try {
      const response = await apiClient.get(`/orders/${orderNumber}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching order ${orderNumber}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  trackOrder: async (orderNumber, email) => {
    try {
      const response = await apiClient.post('/orders/track', {
        orderNumber: orderNumber.trim(),
        email: email.trim()
      });
      return response.data;
    } catch (error) {
      console.error(`Error tracking order ${orderNumber}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Gets the current status of an order
   * Returns order status including payment status
   */
  getOrderStatus: async (orderNumber) => {
    try {
      const response = await apiClient.get(`/orders/${orderNumber}/status`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching order status for ${orderNumber}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Cancels a failed order
   */
  cancelFailedOrder: async (orderNumber) => {
    try {
      const response = await apiClient.post(`/orders/${orderNumber}/cancel`);
      return response.data;
    } catch (error) {
      console.error(`Error cancelling order ${orderNumber}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Verifies payment status with the payment provider
   */
  verifyPayment: async (orderNumber, paymentId) => {
    try {
      const response = await apiClient.post('/payment/verify', {
        orderNumber,
        paymentId
      });
      return response.data;
    } catch (error) {
      console.error(`Error verifying payment for order ${orderNumber}:`, error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Configuration API Endpoints ---
export const configApi = {
  /**
   * Get tax and shipping rates from the backend
   * Returns rates object with taxRate, shippingCost, freeShippingThreshold
   */
  getRates: async () => {
    return dedupedConfigRequest(
      'config-rates',
      async () => {
        try {
          const response = await apiClient.get('/config/rates');

          return response.data; // e.g., { taxRate: 0.18, shippingCost: 80.00, freeShippingThreshold: 500.00 }
        } catch (error) {
          console.error('Error fetching config rates:', error);
          // Return defaults if API fails
          const defaultRates = {
            taxRate: 0.18, // 18% VAT
            shippingCost: 80.00, // Default shipping cost
            freeShippingThreshold: 99999999.00 // Free shipping threshold
          };
          console.warn('Using default rates due to API error:', defaultRates);
          return defaultRates;
        }
      }
    );
  },

  /**
   * Get tax and shipping configuration (more detailed version)
   * This uses the tax-shipping-config endpoint for more comprehensive data
   */
  getTaxShippingConfig: async () => {
    try {
      const response = await apiClient.get('/config/tax-shipping');


      // Transform the response to match our expected format
      return {
        taxRate: response.data.taxRate || response.data.taxRatePercentage / 100,
        taxRatePercentage: response.data.taxRatePercentage,
        shippingCost: response.data.shippingCost,
        freeShippingThreshold: response.data.freeShippingThreshold,
        configName: response.data.configName,
        isActive: response.data.isActive,
        isDefault: response.data.isDefault,
        description: response.data.description
      };
    } catch (error) {
      console.error('Error fetching tax-shipping config:', error);
      // Fallback to basic rates endpoint
      return await configApi.getRates();
    }
  },

  getShippingAnnouncementConfig: async () => {
    const response = await apiClient.get('/config/shipping-announcement');
    return response.data;
  },
  getCartPromotionBadgeConfig: async () => {
    const response = await apiClient.get('/config/cart-promotion-badge');
    return response.data;
  },
};

// --- Discount API Endpoints ---
export const discountApi = {
  /**
   * Get unified discount data for homepage (NEW UNIFIED API)
   * @param {Object} options - Query parameters
   * @returns {Object} Unified discount data with bundles, conditional offers, and campaigns
   */
  getHomepageDiscounts: async (options = {}) => {
    // TEMPORARILY DISABLED - Return empty data structure
    console.log('Homepage discounts temporarily disabled');
    return {
      success: true,
      data: {
        bundles: [],
        conditionalOffers: [],
        campaigns: [],
        meta: { total: 0, hasMore: false }
      }
    };
    
    // Original implementation commented out
    /*
    const params = {
      limit: options.limit || 10,
      currency: options.currency || 'TRY',
      lang: options.lang || 'tr',
      includeInactive: options.includeInactive || false
    };
    
    try {
      const response = await apiClient.get('/discounts/homepage', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching unified discounts:', error);
      throw error.response ? error.response.data : error;
    }
    */
  },

  /**
   * Calculate cart totals with discounts applied
   * @param {Array} cartItems - Array of cart items with productId, quantity, price
   * @param {string} customerEmail - Optional customer email for personalized discounts
   * @returns {Object} Cart calculation with applied discounts
   */
  calculateCartDiscounts: async (cartItems, customerEmail = '', discountCode) => {
    // KEEP ENABLED - This is needed for cart functionality
    try {
      const body = { cartItems, customerEmail };
      if (discountCode) {
        body.discountCode = discountCode;
      }
      const response = await apiClient.post('/cart/calculate', body);
      return response.data;
    } catch (error) {
      console.error('Error calculating cart discounts:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get all active discount campaigns for display
   * @returns {Array} List of active discount campaigns
   */
  getActiveDiscounts: async () => {
    // TEMPORARILY DISABLED - Return empty array
    console.log('Active discounts temporarily disabled');
    return [];
    
    // Original implementation commented out
    /*
    return dedupedRequest(
      'active-discounts',
      async () => {
        try {
          const response = await apiClient.get('/discounts/active');
          
          // Cache version validation - use multiple strategies
          const backendVersion = response.headers['x-discount-version'] || 
                                response.data.version || 
                                response.data.lastModified ||
                                (response.data.length ? `count-${response.data.length}-${JSON.stringify(response.data.map(d => d.id || d.name).sort())}` : null) ||
                                Date.now().toString();
          
          const cachedVersion = localStorage.getItem('discount-cache-version');
          
          if (cachedVersion && cachedVersion !== backendVersion) {
            // Version mismatch - clear all discount caches
            console.log('Discount version mismatch detected, clearing caches');
            discountApi.clearAllDiscountCaches();
          }
          
          // Store new version
          localStorage.setItem('discount-cache-version', backendVersion);
          
          return response.data;
        } catch (error) {
          console.error('Error fetching active discounts:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 60000 } // Cache for 1 minute
    );
    */
  },

  /**
   * Force clear all discount caches when backend changes are detected
   */
  clearAllDiscountCaches: () => {
    try {
      // Clear enhanced cache
      if (window.enhancedCache && window.enhancedCache.invalidateCache) {
        window.enhancedCache.invalidateCache('discount');
      }
      
      // Clear localStorage discount-related items
      if (typeof window !== 'undefined' && window.localStorage) {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('discount') || key.includes('Discount') || key.includes('active-discounts'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
      
      // Clear sessionStorage discount-related items
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.includes('discount') || key.includes('Discount'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
      }
      
      console.log('All discount caches cleared');
      return true;
    } catch (error) {
      console.error('Error clearing discount caches:', error);
      return false;
    }
  },

  /**
   * Get discounts applicable to a specific product
   * @param {string} productId - Product ID to check for discounts
   * @returns {Array} List of applicable discounts for the product
   */
  getProductDiscounts: async (productId) => {
    // TEMPORARILY DISABLED - Return empty array for display purposes
    console.log(`Product discounts temporarily disabled for product ${productId}`);
    return [];
    
    // Original implementation commented out
    /*
    try {
      const response = await apiClient.get(`/discounts/product/${productId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching discounts for product ${productId}:`, error);
      throw error.response ? error.response.data : error;
    }
    */
  },

  /**
   * Get available bundle opportunities
   * @returns {Array} List of bundle discount opportunities
   */
  getBundleOpportunities: async () => {
    // TEMPORARILY DISABLED - Return empty array for display purposes
    console.log('Bundle opportunities temporarily disabled');
    return [];
    
    // Original implementation commented out
    /*
    try {
      const response = await apiClient.get('/discounts/bundle-opportunities');
      return response.data;
    } catch (error) {
      console.error('Error fetching bundle opportunities:', error);
      throw error.response ? error.response.data : error;
    }
    */
  },

  /**
   * Get conditional discount opportunities
   * @returns {Array} List of conditional discount opportunities
   */
  getConditionalOpportunities: async () => {
    // TEMPORARILY DISABLED - Return empty array for display purposes
    console.log('Conditional opportunities temporarily disabled');
    return [];
    
    // Original implementation commented out
    /*
    try {
      const response = await apiClient.get('/discounts/conditional-opportunities');
      return response.data;
    } catch (error) {
      console.error('Error fetching conditional opportunities:', error);
      throw error.response ? error.response.data : error;
    }
    */
  },

  /**
   * Preview discount application without applying
   * @param {Array} cartItems - Cart items to preview discounts for
   * @param {string} customerEmail - Optional customer email
   * @returns {Object} Preview of discount application
   */
  previewDiscounts: async (cartItems, customerEmail = '') => {
    try {
      const response = await apiClient.post('/discounts/preview', {
        cartItems,
        customerEmail
      });
      return response.data;
    } catch (error) {
      console.error('Error previewing discounts:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Admin Discount API Endpoints (for future admin panel) ---
export const adminDiscountApi = {
  /**
   * Get all discount rules (admin only)
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @param {string} sort - Sort parameter
   * @returns {Object} Paginated discount rules
   */
  getAllDiscountRules: async (page = 0, size = 10, sort = 'createdAt,desc') => {
    try {
      const response = await apiClient.get('/admin/discounts', {
        params: { page, size, sort },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching discount rules:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Create new discount rule (admin only)
   * @param {Object} discountData - Discount rule data
   * @returns {Object} Created discount rule
   */
  createDiscountRule: async (discountData) => {
    try {
      const response = await apiClient.post('/admin/discounts', discountData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating discount rule:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update discount rule (admin only)
   * @param {string} discountId - Discount rule ID
   * @param {Object} discountData - Updated discount data
   * @returns {Object} Updated discount rule
   */
  updateDiscountRule: async (discountId, discountData) => {
    try {
      const response = await apiClient.put(`/admin/discounts/${discountId}`, discountData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating discount rule ${discountId}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Delete discount rule (admin only)
   * @param {string} discountId - Discount rule ID
   * @returns {boolean} Success status
   */
  deleteDiscountRule: async (discountId) => {
    try {
      const response = await apiClient.delete(`/admin/discounts/${discountId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.status === 204;
    } catch (error) {
      console.error(`Error deleting discount rule ${discountId}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Toggle discount rule status (admin only)
   * @param {string} discountId - Discount rule ID
   * @returns {Object} Updated discount rule
   */
  toggleDiscountStatus: async (discountId) => {
    try {
      const response = await apiClient.patch(`/admin/discounts/${discountId}/toggle`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error toggling discount status ${discountId}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get discount statistics (admin only)
   * @returns {Object} Discount usage statistics
   */
  getDiscountStatistics: async () => {
    try {
      const response = await apiClient.get('/admin/discounts/statistics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching discount statistics:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Validate discount rule (admin only)
   * @param {Object} discountData - Discount rule to validate
   * @returns {Object} Validation result
   */
  validateDiscountRule: async (discountData) => {
    try {
      const response = await apiClient.post('/admin/discounts/validate', discountData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error validating discount rule:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Popular Products API Endpoints ---
export const popularProductsApi = {
  /**
   * Get all active popular products
   * @returns {Array} List of active popular products
   */
  getPopularProducts: async () => {
    return dedupedRequest(
      'popular-products',
      async () => {
        try {
          const response = await apiClient.get('/popular-products');
          return response.data;
        } catch (error) {
          console.error('Error fetching popular products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 60000 } // Cache for 1 minute
    );
  },

  /**
   * Get popular products with pagination
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @param {string} sort - Sort criteria
   * @returns {Object} Paginated popular products
   */
  getPopularProductsPaginated: async (page = 0, size = 12, sort = 'displayOrder,asc') => {
    try {
      const response = await apiClient.get('/popular-products/paginated', {
        params: { page, size, sort }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching paginated popular products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get featured popular products
   * @returns {Array} List of featured popular products
   */
  getFeaturedProducts: async () => {
    try {
      const response = await apiClient.get('/popular-products/featured');
      return response.data;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get promoted popular products
   * @returns {Array} List of promoted popular products
   */
  getPromotedProducts: async () => {
    try {
      const response = await apiClient.get('/popular-products/promoted');
      return response.data;
    } catch (error) {
      console.error('Error fetching promoted products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get top N popular products
   * @param {number} count - Number of products to fetch (max 100)
   * @returns {Array} List of top popular products
   */
  getTopProducts: async (count = 10) => {
    try {
      const response = await apiClient.get(`/popular-products/top/${count}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching top products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get carousel popular products (up to 8)
   * @returns {Array} List of carousel popular products
   */
  getCarouselProducts: async () => {
    try {
      const response = await apiClient.get('/popular-products/carousel');
      return response.data;
    } catch (error) {
      console.error('Error fetching carousel products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get hero popular products (top 3)
   * @returns {Array} List of hero popular products
   */
  getHeroProducts: async () => {
    try {
      const response = await apiClient.get('/popular-products/hero');
      return response.data;
    } catch (error) {
      console.error('Error fetching hero products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get sidebar popular products (up to 5)
   * @returns {Array} List of sidebar popular products
   */
  getSidebarProducts: async () => {
    try {
      const response = await apiClient.get('/popular-products/sidebar');
      return response.data;
    } catch (error) {
      console.error('Error fetching sidebar products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get popular products with promotions
   * @returns {Array} List of popular products with featured or promotion text
   */
  getProductsWithPromotions: async () => {
    try {
      const response = await apiClient.get('/popular-products/with-promotions');
      return response.data;
    } catch (error) {
      console.error('Error fetching products with promotions:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Search popular products
   * @param {string} query - Search query
   * @returns {Array} List of matching popular products
   */
  searchPopularProducts: async (query) => {
    try {
      const response = await apiClient.get('/popular-products/search', {
        params: { query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching popular products:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Recommended Products API Endpoints ---
export const recommendedProductsApi = {
  /**
   * Get all active recommended products
   * @returns {Array} List of active recommended products
   */
  getRecommendedProducts: async () => {
    return dedupedRequest(
      'recommended-products',
      async () => {
        try {
          const response = await apiClient.get('/recommended-products');
          return response.data;
        } catch (error) {
          console.error('Error fetching recommended products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 60000 } // Cache for 1 minute
    );
  },

  /**
   * Get recommended products with pagination
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @param {string} sort - Sort criteria
   * @param {string} direction - Sort direction (ASC/DESC)
   * @returns {Object} Paginated recommended products
   */
  getRecommendedProductsPaginated: async (page = 0, size = 12, sort = 'displayOrder', direction = 'ASC') => {
    try {
      const response = await apiClient.get('/recommended-products/paginated', {
        params: { page, size, sort, direction }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching paginated recommended products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get top N recommended products
   * @param {number} count - Number of products to fetch (1-100)
   * @returns {Array} List of top recommended products
   */
  getTopRecommendedProducts: async (count = 10) => {
    try {
      const response = await apiClient.get(`/recommended-products/top/${count}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching top recommended products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get recommended products for carousel
   * @returns {Array} List of carousel recommended products (up to 8)
   */
  getCarouselRecommendedProducts: async () => {
    return dedupedRequest(
      'recommended-products-carousel',
      async () => {
        try {
          const response = await apiClient.get('/recommended-products/carousel');
          return response.data;
        } catch (error) {
          console.error('Error fetching carousel recommended products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 120000 } // Cache for 2 minutes
    );
  },

  /**
   * Get hero section recommended products
   * @returns {Array} List of hero recommended products (top 3)
   */
  getHeroRecommendedProducts: async () => {
    return dedupedRequest(
      'recommended-products-hero',
      async () => {
        try {
          const response = await apiClient.get('/recommended-products/hero');
          return response.data;
        } catch (error) {
          console.error('Error fetching hero recommended products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 300000 } // Cache for 5 minutes
    );
  },

  /**
   * Get sidebar recommended products
   * @returns {Array} List of sidebar recommended products (up to 5)
   */
  getSidebarRecommendedProducts: async () => {
    return dedupedRequest(
      'recommended-products-sidebar',
      async () => {
        try {
          const response = await apiClient.get('/recommended-products/sidebar');
          return response.data;
        } catch (error) {
          console.error('Error fetching sidebar recommended products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 180000 } // Cache for 3 minutes
    );
  },

  /**
   * Get featured recommended products
   * @returns {Array} List of featured recommended products
   */
  getFeaturedRecommendedProducts: async () => {
    return dedupedRequest(
      'recommended-products-featured',
      async () => {
        try {
          const response = await apiClient.get('/recommended-products/featured');
          return response.data;
        } catch (error) {
          console.error('Error fetching featured recommended products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 300000 } // Cache for 5 minutes
    );
  }
};

// --- Admin Popular Products API Endpoints ---
export const adminPopularProductsApi = {
  /**
   * Get all popular products (including inactive) for admin
   * @returns {Array} List of all popular products
   */
  getAllPopularProducts: async () => {
    try {
      const response = await apiClient.get('/admin/popular-products', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching all popular products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get popular product by ID
   * @param {string} id - Popular product ID
   * @returns {Object} Popular product details
   */
  getPopularProductById: async (id) => {
    try {
      const response = await apiClient.get(`/admin/popular-products/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching popular product:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Create new popular product
   * @param {Object} popularProductData - Popular product data
   * @returns {Object} Created popular product
   */
  createPopularProduct: async (popularProductData) => {
    try {
      const response = await apiClient.post('/admin/popular-products', popularProductData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating popular product:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update popular product
   * @param {string} id - Popular product ID
   * @param {Object} updateData - Update data
   * @returns {Object} Updated popular product
   */
  updatePopularProduct: async (id, updateData) => {
    try {
      const response = await apiClient.put(`/admin/popular-products/${id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating popular product:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Delete popular product
   * @param {string} id - Popular product ID
   * @returns {void}
   */
  deletePopularProduct: async (id) => {
    try {
      await apiClient.delete(`/admin/popular-products/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
    } catch (error) {
      console.error('Error deleting popular product:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Toggle popular product active status
   * @param {string} id - Popular product ID
   * @returns {Object} Updated popular product
   */
  toggleActiveStatus: async (id) => {
    try {
      const response = await apiClient.patch(`/admin/popular-products/${id}/toggle-active`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error toggling active status:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Reorder popular products
   * @param {Array} orderIds - Array of popular product IDs in new order
   * @returns {Array} Updated popular products
   */
  reorderPopularProducts: async (orderIds) => {
    try {
      const response = await apiClient.post('/admin/popular-products/reorder', orderIds, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error reordering popular products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get next available display order
   * @returns {number} Next display order
   */
  getNextDisplayOrder: async () => {
    try {
      const response = await apiClient.get('/admin/popular-products/next-display-order', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting next display order:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Check if product is already popular
   * @param {string} productId - Product ID
   * @returns {boolean} Whether product is popular
   */
  isProductPopular: async (productId) => {
    try {
      const response = await apiClient.get(`/admin/popular-products/product/${productId}/is-popular`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking if product is popular:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Bulk create popular products
   * @param {Array} popularProductsData - Array of popular product data
   * @returns {Array} Created popular products
   */
  bulkCreatePopularProducts: async (popularProductsData) => {
    try {
      const response = await apiClient.post('/admin/popular-products/bulk-create', popularProductsData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk creating popular products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Bulk delete popular products
   * @param {Array} ids - Array of popular product IDs
   * @returns {void}
   */
  bulkDeletePopularProducts: async (ids) => {
    try {
      await apiClient.delete('/admin/popular-products/bulk-delete', {
        data: ids,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
    } catch (error) {
      console.error('Error bulk deleting popular products:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Discounted Products API Endpoints ---
export const discountedProductsApi = {
  /**
   * Get all discounted products
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sort - Sort parameter
   * @returns {Object} Discounted products with pagination and summary
   */
  getDiscountedProducts: async (page = 0, size = 20, sort = 'totalDiscountPercentage,desc') => {
    const cacheKey = `discounted-products-${page}-${size}-${sort}`;
    
    return dedupedRequest(
      cacheKey,
      async () => {
        try {
          const response = await apiClient.get('/discounted-products', {
            params: { page, size, sort }
          });
          return response.data;
        } catch (error) {
          console.error('Error fetching discounted products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 30000 } // Cache for 30 seconds
    );
  },

  /**
   * Get discounted products by category
   * @param {string} categoryId - Category UUID
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sort - Sort parameter
   * @returns {Object} Discounted products filtered by category
   */
  getDiscountedProductsByCategory: async (categoryId, page = 0, size = 20, sort = 'totalDiscountPercentage,desc') => {
    try {
      const response = await apiClient.get(`/discounted-products/category/${categoryId}`, {
        params: { page, size, sort }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching discounted products for category ${categoryId}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get discounted products by discount type
   * @param {string} discountType - Type of discount (BULK, PRODUCT, BUNDLE, CONDITIONAL)
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sort - Sort parameter
   * @returns {Object} Discounted products filtered by discount type
   */
  getDiscountedProductsByType: async (discountType, page = 0, size = 20, sort = 'totalDiscountPercentage,desc') => {
    try {
      const response = await apiClient.get(`/discounted-products/type/${discountType}`, {
        params: { page, size, sort }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching discounted products for type ${discountType}:`, error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Campaign API Endpoints ---
export const campaignApi = {
  /**
   * Get all active campaigns
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sort - Sort parameter
   * @returns {Object} Active campaigns with pagination
   */
  getAllCampaigns: async (page = 0, size = 20, sort = 'startDate,desc') => {
    try {
      const response = await apiClient.get('/campaigns', {
        params: { page, size, sort }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get campaign by ID
   * @param {string} campaignId - Campaign ID
   * @returns {Object} Campaign details
   */
  getCampaignById: async (campaignId) => {
    try {
      const response = await apiClient.get(`/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching campaign ${campaignId}:`, error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get campaigns by type
   * @param {string} campaignType - Campaign type (BUNDLE_DISCOUNT, CONDITIONAL_DISCOUNT)
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sort - Sort parameter
   * @returns {Object} Campaigns filtered by type
   */
  getCampaignsByType: async (campaignType, page = 0, size = 20, sort = 'startDate,desc') => {
    try {
      const response = await apiClient.get(`/campaigns/type/${campaignType}`, {
        params: { page, size, sort }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching campaigns for type ${campaignType}:`, error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- New Added Products API Endpoints ---
export const newAddedProductsApi = {
  /**
   * Get all active new added products
   * @returns {Array} List of active new added products
   */
  getNewAddedProducts: async () => {
    return dedupedRequest(
      'new-added-products',
      async () => {
        try {
          const response = await apiClient.get('/new-added-products');
          return response.data;
        } catch (error) {
          console.error('Error fetching new added products:', error);
          throw error.response ? error.response.data : error;
        }
      },
      { ttl: 60000 } // Cache for 1 minute
    );
  },

  /**
   * Get new added products with pagination
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @param {string} sort - Sort criteria
   * @returns {Object} Paginated new added products
   */
  getNewAddedProductsPaginated: async (page = 0, size = 12, sort = 'displayOrder,asc') => {
    try {
      const response = await apiClient.get('/new-added-products/paginated', {
        params: { page, size, sort }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching paginated new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get featured new added products
   * @returns {Array} List of featured new added products
   */
  getFeaturedProducts: async () => {
    try {
      const response = await apiClient.get('/new-added-products/featured');
      return response.data;
    } catch (error) {
      console.error('Error fetching featured new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get promoted new added products
   * @returns {Array} List of promoted new added products
   */
  getPromotedProducts: async () => {
    try {
      const response = await apiClient.get('/new-added-products/promoted');
      return response.data;
    } catch (error) {
      console.error('Error fetching promoted new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get top N new added products
   * @param {number} count - Number of products to fetch (max 100)
   * @returns {Array} List of top new added products
   */
  getTopProducts: async (count = 10) => {
    try {
      const response = await apiClient.get(`/new-added-products/top/${count}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching top new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get carousel new added products (up to 8)
   * @returns {Array} List of carousel new added products
   */
  getCarouselProducts: async () => {
    try {
      const response = await apiClient.get('/new-added-products/carousel');
      return response.data;
    } catch (error) {
      console.error('Error fetching carousel new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get hero new added products (top 3)
   * @returns {Array} List of hero new added products
   */
  getHeroProducts: async () => {
    try {
      const response = await apiClient.get('/new-added-products/hero');
      return response.data;
    } catch (error) {
      console.error('Error fetching hero new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get sidebar new added products (up to 5)
   * @returns {Array} List of sidebar new added products
   */
  getSidebarProducts: async () => {
    try {
      const response = await apiClient.get('/new-added-products/sidebar');
      return response.data;
    } catch (error) {
      console.error('Error fetching sidebar new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get grid new added products (12 products)
   * @returns {Array} List of grid new added products
   */
  getGridProducts: async () => {
    try {
      const response = await apiClient.get('/new-added-products/grid');
      return response.data;
    } catch (error) {
      console.error('Error fetching grid new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get recent new added products (top 6)
   * @returns {Array} List of recent new added products
   */
  getRecentProducts: async () => {
    try {
      const response = await apiClient.get('/new-added-products/recent');
      return response.data;
    } catch (error) {
      console.error('Error fetching recent new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get new added products with promotions
   * @returns {Array} List of new added products with featured or promotion text
   */
  getProductsWithPromotions: async () => {
    try {
      const response = await apiClient.get('/new-added-products/with-promotions');
      return response.data;
    } catch (error) {
      console.error('Error fetching new added products with promotions:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Search new added products
   * @param {string} query - Search query
   * @returns {Array} List of matching new added products
   */
  searchNewAddedProducts: async (query) => {
    try {
      const response = await apiClient.get('/new-added-products/search', {
        params: { query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get count of new added products
   * @returns {number} Total count of new added products
   */
  getCount: async () => {
    try {
      const response = await apiClient.get('/new-added-products/count');
      return response.data;
    } catch (error) {
      console.error('Error fetching new added products count:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Admin New Added Products API Endpoints ---
export const adminNewAddedProductsApi = {
  /**
   * Get all new added products (including inactive) for admin
   * @returns {Array} List of all new added products
   */
  getAllNewAddedProducts: async () => {
    try {
      const response = await apiClient.get('/admin/new-added-products');
      return response.data;
    } catch (error) {
      console.error('Error fetching all new added products for admin:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Create a new added product
   * @param {Object} productData - New added product data
   * @returns {Object} Created new added product
   */
  createNewAddedProduct: async (productData) => {
    try {
      const response = await apiClient.post('/admin/new-added-products', productData);
      return response.data;
    } catch (error) {
      console.error('Error creating new added product:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update a new added product
   * @param {string} id - New added product ID
   * @param {Object} productData - Updated product data
   * @returns {Object} Updated new added product
   */
  updateNewAddedProduct: async (id, productData) => {
    try {
      const response = await apiClient.put(`/admin/new-added-products/${id}`, productData);
      return response.data;
    } catch (error) {
      console.error('Error updating new added product:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Delete a new added product
   * @param {string} id - New added product ID
   * @returns {void}
   */
  deleteNewAddedProduct: async (id) => {
    try {
      await apiClient.delete(`/admin/new-added-products/${id}`);
    } catch (error) {
      console.error('Error deleting new added product:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Toggle active status of a new added product
   * @param {string} id - New added product ID
   * @returns {Object} Updated new added product
   */
  toggleActiveStatus: async (id) => {
    try {
      const response = await apiClient.patch(`/admin/new-added-products/${id}/toggle-active`);
      return response.data;
    } catch (error) {
      console.error('Error toggling new added product status:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Reorder new added products
   * @param {Array} orderedIds - Array of product IDs in new order
   * @returns {void}
   */
  reorderProducts: async (orderedIds) => {
    try {
      await apiClient.post('/admin/new-added-products/reorder', orderedIds);
    } catch (error) {
      console.error('Error reordering new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Check if product is already in new added products
   * @param {string} productId - Product ID to check
   * @returns {boolean} Whether product is already added
   */
  isProductNewAdded: async (productId) => {
    try {
      const response = await apiClient.get(`/admin/new-added-products/product/${productId}/is-new-added`);
      return response.data;
    } catch (error) {
      console.error('Error checking if product is new added:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get next display order
   * @returns {Object} Next display order number
   */
  getNextDisplayOrder: async () => {
    try {
      const response = await apiClient.get('/admin/new-added-products/next-display-order');
      return response.data;
    } catch (error) {
      console.error('Error getting next display order:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Bulk create new added products
   * @param {Array} productsData - Array of new added product data
   * @returns {Array} Created new added products
   */
  bulkCreateProducts: async (productsData) => {
    try {
      const response = await apiClient.post('/admin/new-added-products/bulk-create', productsData);
      return response.data;
    } catch (error) {
      console.error('Error bulk creating new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Bulk delete new added products
   * @param {Array} productIds - Array of product IDs to delete
   * @returns {void}
   */
  bulkDeleteProducts: async (productIds) => {
    try {
      await apiClient.delete('/admin/new-added-products/bulk-delete', { data: productIds });
    } catch (error) {
      console.error('Error bulk deleting new added products:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get statistics for new added products
   * @returns {Object} Statistics data
   */
  getStatistics: async () => {
    try {
      const response = await apiClient.get('/admin/new-added-products/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching new added products statistics:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Authentication API Endpoints ---
export const authApi = {
  /**
   * User registration
   * @param {Object} userData - User registration data
   * @returns {Object} Response with token, email, and fullName
   */
  register: async (userData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      
      // Don't store tokens during registration - user should login manually
      // This is for security - registration and login should be separate steps
      
      return response.data;
    } catch (error) {
      console.error('Error during registration:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * User login
   * @param {Object} credentials - Login credentials
   * @returns {Object} Response with token, email, and fullName
   */
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      
      // Store tokens if login is successful
      if (response.data.token || response.data.accessToken) {
        const accessToken = response.data.token || response.data.accessToken;
        const refreshToken = response.data.refreshToken;
        
        storeTokens(accessToken, refreshToken);
        
        // Update API client headers
        apiClient.defaults.headers.Authorization = `Bearer ${accessToken}`;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error during login:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * User logout
   */
  logout: () => {
    clearTokens();
    delete apiClient.defaults.headers.Authorization;
  },

  /**
   * Email verification
   * @param {string} token - Verification token from email
   * @returns {Object} Verification response
   */
  verifyEmail: async (token) => {
    try {
      const response = await apiClient.post('/auth/verify-email', { token });
      return response.data;
    } catch (error) {
      console.error('Error verifying email:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Resend verification email
   * @param {string} email - User email
   * @returns {Object} Response
   */
  resendVerification: async (email) => {
    try {
      const response = await apiClient.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Object} Response
   */
  forgotPassword: async (email) => {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Reset password
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Object} Response
   */
  resetPassword: async (token, newPassword) => {
    try {
      const response = await apiClient.post('/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated: () => {
    const token = getAccessToken();
    return token && !isTokenExpired(token);
  },

  /**
   * Get stored auth token
   * @returns {string|null} Auth token
   */
  getToken: () => {
    return getAccessToken();
  },

  /**
   * Set auth token and update headers
   * @param {string} token - JWT token
   * @param {string} refreshToken - Refresh token (optional)
   */
  setToken: (token, refreshToken = null) => {
    storeTokens(token, refreshToken);
    apiClient.defaults.headers.Authorization = `Bearer ${token}`;
  },

  /**
   * Refresh access token
   * @param {string} refreshToken - Refresh token
   * @returns {Object} New tokens
   */
  refreshToken: async (refreshToken) => {
    try {
      const response = await apiClient.post('/auth/refresh', { refreshToken });
      
      // Store new tokens if refresh is successful
      if (response.data.accessToken || response.data.token) {
        const accessToken = response.data.accessToken || response.data.token;
        const newRefreshToken = response.data.refreshToken || refreshToken;
        
        storeTokens(accessToken, newRefreshToken);
        apiClient.defaults.headers.Authorization = `Bearer ${accessToken}`;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Clear tokens on refresh failure
      clearTokens();
      delete apiClient.defaults.headers.Authorization;
      throw error.response ? error.response.data : error;
    }
  }
};

// --- User Profile API Endpoints ---
export const userApi = {
  /**
   * Get user profile
   * @returns {Object} User profile data
   */
  getProfile: async () => {
    try {
      const response = await apiClient.get('/user/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update user profile
   * @param {Object} profileData - Profile update data
   * @returns {Object} Updated profile data
   */
  updateProfile: async (profileData) => {
    try {
      const response = await apiClient.put('/user/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Change password
   * @param {Object} passwordData - Current and new password
   * @returns {Object} Response
   */
  changePassword: async (passwordData) => {
    try {
      const response = await apiClient.put('/user/password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update marketing preferences
   * @param {Object} preferences - Marketing preferences
   * @returns {Object} Updated preferences
   */
  updateMarketingPreferences: async (preferences) => {
    try {
      const response = await apiClient.put('/user/marketing-preferences', preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating marketing preferences:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get dashboard statistics
   * @returns {Object} Dashboard stats
   */
  getDashboardStats: async () => {
    try {
      const response = await apiClient.get('/user/dashboard-stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Address Management API Endpoints ---
export const addressApi = {
  /**
   * Get all user addresses
   * @returns {Array} User addresses
   */
  getAddresses: async () => {
    try {
      const response = await apiClient.get('/user/addresses');
      return response.data;
    } catch (error) {
      console.error('Error fetching addresses:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Create new address
   * @param {Object} addressData - Address data
   * @returns {Object} Created address
   */
  createAddress: async (addressData) => {
    try {
      const response = await apiClient.post('/user/addresses', addressData);
      return response.data;
    } catch (error) {
      console.error('Error creating address:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update address
   * @param {string} addressId - Address ID
   * @param {Object} addressData - Updated address data
   * @returns {Object} Updated address
   */
  updateAddress: async (addressId, addressData) => {
    try {
      const response = await apiClient.put(`/user/addresses/${addressId}`, addressData);
      return response.data;
    } catch (error) {
      console.error('Error updating address:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Delete address
   * @param {string} addressId - Address ID
   * @returns {Object} Response
   */
  deleteAddress: async (addressId) => {
    try {
      const response = await apiClient.delete(`/user/addresses/${addressId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting address:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Set default address
   * @param {string} addressId - Address ID
   * @returns {Object} Response
   */
  setDefaultAddress: async (addressId) => {
    try {
      const response = await apiClient.put(`/user/addresses/${addressId}/default`);
      return response.data;
    } catch (error) {
      console.error('Error setting default address:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get default address
   * @returns {Object} Default address
   */
  getDefaultAddress: async () => {
    try {
      const response = await apiClient.get('/user/addresses/default');
      return response.data;
    } catch (error) {
      console.error('Error fetching default address:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Favorites API Endpoints ---
export const favoritesApi = {
  /**
   * Get user favorites
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @returns {Object} Paginated favorites
   */
  getFavorites: async (page = 0, size = 20) => {
    try {
      const response = await apiClient.get('/user/favorites', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching favorites:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Add product to favorites
   * @param {string} productId - Product ID
   * @returns {Object} Response
   */
  addToFavorites: async (productId) => {
    try {
      const response = await apiClient.post(`/user/favorites/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Remove from favorites
   * @param {string} productId - Product ID
   * @returns {Object} Response
   */
  removeFromFavorites: async (productId) => {
    try {
      const response = await apiClient.delete(`/user/favorites/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Toggle favorite status
   * @param {string} productId - Product ID
   * @returns {Object} Response
   */
  toggleFavorite: async (productId) => {
    try {
      const response = await apiClient.post(`/user/favorites/${productId}/toggle`);
      return response.data;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Check if product is favorite
   * @param {string} productId - Product ID
   * @returns {Object} Favorite status
   */
  checkFavorite: async (productId) => {
    try {
      const response = await apiClient.get(`/user/favorites/check/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking favorite status:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get favorite product IDs
   * @returns {Array} Product IDs
   */
  getFavoriteIds: async () => {
    try {
      const response = await apiClient.get('/user/favorites/ids');
      return response.data;
    } catch (error) {
      console.error('Error fetching favorite IDs:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get favorite count
   * @returns {Object} Favorite count
   */
  getFavoriteCount: async () => {
    try {
      const response = await apiClient.get('/user/favorites/count');
      return response.data;
    } catch (error) {
      console.error('Error fetching favorite count:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Custom Lists API Endpoints ---
export const listsApi = {
  /**
   * Get all user lists
   * @returns {Array} User lists
   */
  getLists: async () => {
    try {
      const response = await apiClient.get('/user/lists');
      return response.data;
    } catch (error) {
      console.error('Error fetching lists:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get user lists (alias for getLists - used by AddToListButton)
   * @returns {Object} Response with lists array
   */
  getUserLists: async () => {
    try {
      const response = await apiClient.get('/user/lists');
      // Wrap the response to match AddToListButton expectations
      return {
        lists: response.data || []
      };
    } catch (error) {
      console.error('Error fetching user lists:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get lists with items
   * @returns {Array} User lists with items
   */
  getListsWithItems: async () => {
    try {
      const response = await apiClient.get('/user/lists/with-items');
      return response.data;
    } catch (error) {
      console.error('Error fetching lists with items:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Create new list
   * @param {Object} listData - List data
   * @returns {Object} Created list
   */
  createList: async (listData) => {
    try {
      const response = await apiClient.post('/user/lists', listData);
      return response.data;
    } catch (error) {
      console.error('Error creating list:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update list
   * @param {string} listId - List ID
   * @param {Object} listData - Updated list data
   * @returns {Object} Updated list
   */
  updateList: async (listId, listData) => {
    try {
      const response = await apiClient.put(`/user/lists/${listId}`, listData);
      return response.data;
    } catch (error) {
      console.error('Error updating list:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Delete list
   * @param {string} listId - List ID
   * @returns {Object} Response
   */
  deleteList: async (listId) => {
    try {
      const response = await apiClient.delete(`/user/lists/${listId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting list:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get list items
   * @param {string} listId - List ID
   * @returns {Array} List items
   */
  getListItems: async (listId) => {
    try {
      const response = await apiClient.get(`/user/lists/${listId}/items`);
      return response.data;
    } catch (error) {
      console.error('Error fetching list items:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Add item to list
   * @param {string} listId - List ID
   * @param {Object} itemData - Item data
   * @returns {Object} Response
   */
  addItemToList: async (listId, itemData) => {
    try {
      const response = await apiClient.post(`/user/lists/${listId}/items`, itemData);
      return response.data;
    } catch (error) {
      console.error('Error adding item to list:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Add product to list (alias for addItemToList - used by AddToListButton)
   * @param {string} listId - List ID
   * @param {Object} productData - Product data with productId
   * @returns {Object} Response
   */
  addProductToList: async (listId, productData) => {
    try {
      const response = await apiClient.post(`/user/lists/${listId}/items`, productData);
      return response.data;
    } catch (error) {
      console.error('Error adding product to list:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Remove item from list
   * @param {string} listId - List ID
   * @param {string} productId - Product ID
   * @returns {Object} Response
   */
  removeItemFromList: async (listId, productId) => {
    try {
      const response = await apiClient.delete(`/user/lists/${listId}/items/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing item from list:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update item notes
   * @param {string} listId - List ID
   * @param {string} productId - Product ID
   * @param {Object} notesData - Notes data
   * @returns {Object} Response
   */
  updateItemNotes: async (listId, productId, notesData) => {
    try {
      const response = await apiClient.put(`/user/lists/${listId}/items/${productId}/notes`, notesData);
      return response.data;
    } catch (error) {
      console.error('Error updating item notes:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Reorder list items
   * @param {string} listId - List ID
   * @param {Array} itemIds - Item IDs in new order
   * @returns {Object} Response
   */
  reorderListItems: async (listId, itemIds) => {
    try {
      const response = await apiClient.put(`/user/lists/${listId}/items/reorder`, { itemIds });
      return response.data;
    } catch (error) {
      console.error('Error reordering list items:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Cart API Endpoints (for cart merging and syncing) ---
export const cartApi = {
  /**
   * Get user's saved cart
   * @returns {Object} User cart data
   */
  getCart: async () => {
    try {
      const response = await apiClient.get('/user/cart');
      return response.data;
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Sync cart with server
   * @param {Array} cartItems - Cart items to sync
   * @returns {Object} Response
   */
  syncCart: async (cartItems) => {
    try {
      const response = await apiClient.post('/user/cart/sync', { items: cartItems });
      return response.data;
    } catch (error) {
      console.error('Error syncing cart:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Merge anonymous cart with user cart
   * @param {Array} anonymousCartItems - Anonymous cart items
   * @returns {Object} Merged cart data
   */
  mergeCart: async (anonymousCartItems) => {
    try {
      const response = await apiClient.post('/user/cart/merge', { anonymousItems: anonymousCartItems });
      return response.data;
    } catch (error) {
      console.error('Error merging cart:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- User Cart API Endpoints ---
export const userCartApi = {
  /**
   * Get user cart
   * @returns {Object} User cart
   */
  getCart: async () => {
    try {
      const response = await apiClient.get('/user/cart');
      return response.data;
    } catch (error) {
      console.error('Error fetching user cart:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Add item to cart
   * @param {Object} itemData - Item data
   * @returns {Object} Response
   */
  addItem: async (itemData) => {
    try {
      const response = await apiClient.post('/user/cart/items', itemData);
      return response.data;
    } catch (error) {
      console.error('Error adding item to cart:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update cart item quantity
   * @param {string} itemId - Item ID
   * @param {Object} quantityData - Quantity data
   * @returns {Object} Response
   */
  updateItemQuantity: async (itemId, quantityData) => {
    try {
      const response = await apiClient.put(`/user/cart/items/${itemId}`, quantityData);
      return response.data;
    } catch (error) {
      console.error('Error updating cart item quantity:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Remove item from cart
   * @param {string} itemId - Item ID
   * @returns {Object} Response
   */
  removeItem: async (itemId) => {
    try {
      const response = await apiClient.delete(`/user/cart/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing item from cart:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Clear cart
   * @returns {Object} Response
   */
  clearCart: async () => {
    try {
      const response = await apiClient.delete('/user/cart');
      return response.data;
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get cart total
   * @returns {Object} Cart total
   */
  getCartTotal: async () => {
    try {
      const response = await apiClient.get('/user/cart/total');
      return response.data;
    } catch (error) {
      console.error('Error fetching cart total:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Merge anonymous cart with user cart
   * @param {Object} sessionData - Session data
   * @returns {Object} Response
   */
  mergeCart: async (sessionData) => {
    try {
      const response = await apiClient.post('/user/cart/merge', sessionData);
      return response.data;
    } catch (error) {
      console.error('Error merging cart:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- User Orders API Endpoints ---
export const userOrdersApi = {
  /**
   * Get order history
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @returns {Object} Paginated orders
   */
  getOrders: async (page = 0, size = 10) => {
    try {
      const response = await apiClient.get('/user/orders', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get specific order
   * @param {string} orderId - Order ID
   * @returns {Object} Order details
   */
  getOrder: async (orderId) => {
    try {
      const response = await apiClient.get(`/user/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get order by order number
   * @param {string} orderNumber - Order number
   * @returns {Object} Order details
   */
  getOrderByNumber: async (orderNumber) => {
    try {
      const response = await apiClient.get(`/user/orders/number/${orderNumber}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order by number:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Search orders
   * @param {Object} searchParams - Search parameters
   * @returns {Object} Search results
   */
  searchOrders: async (searchParams) => {
    try {
      const response = await apiClient.get('/user/orders/search', {
        params: searchParams
      });
      return response.data;
    } catch (error) {
      console.error('Error searching orders:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get orders by status
   * @param {string} status - Order status
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @returns {Object} Orders by status
   */
  getOrdersByStatus: async (status, page = 0, size = 10) => {
    try {
      const response = await apiClient.get(`/user/orders/status/${status}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching orders by status:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Reorder all items from an order
   * @param {string} orderId - Order ID
   * @returns {Object} Response
   */
  reorderAll: async (orderId) => {
    try {
      const response = await apiClient.post(`/user/orders/${orderId}/reorder`);
      return response.data;
    } catch (error) {
      console.error('Error reordering all items:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Reorder specific items from an order
   * @param {string} orderId - Order ID
   * @param {Array} productIds - Product IDs to reorder
   * @returns {Object} Response
   */
  reorderItems: async (orderId, productIds) => {
    try {
      const response = await apiClient.post(`/user/orders/${orderId}/reorder/items`, { productIds });
      return response.data;
    } catch (error) {
      console.error('Error reordering specific items:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Check if order can be reordered
   * @param {string} orderId - Order ID
   * @returns {Object} Reorder availability
   */
  checkReorderAvailability: async (orderId) => {
    try {
      const response = await apiClient.get(`/user/orders/${orderId}/reorder/check`);
      return response.data;
    } catch (error) {
      console.error('Error checking reorder availability:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get recent orders
   * @param {number} limit - Number of orders to fetch
   * @returns {Array} Recent orders
   */
  getRecentOrders: async (limit = 5) => {
    try {
      const response = await apiClient.get('/user/orders/recent', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get order count
   * @returns {Object} Order count
   */
  getOrderCount: async () => {
    try {
      const response = await apiClient.get('/user/orders/count');
      return response.data;
    } catch (error) {
      console.error('Error fetching order count:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// --- Consent Management API Endpoints ---
export const consentApi = {
  /**
   * Get consent preferences
   * @returns {Object} Consent preferences
   */
  getConsents: async () => {
    try {
      const response = await apiClient.get('/user/consent');
      return response.data;
    } catch (error) {
      console.error('Error fetching consents:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Update commercial communication consents
   * @param {Object} consentData - Consent data
   * @returns {Object} Response
   */
  updateCommercialConsents: async (consentData) => {
    try {
      const response = await apiClient.put('/user/consent/commercial-communications', consentData);
      return response.data;
    } catch (error) {
      console.error('Error updating commercial consents:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Withdraw all marketing consents
   * @returns {Object} Response
   */
  withdrawAllMarketing: async () => {
    try {
      const response = await apiClient.post('/user/consent/withdraw-all-marketing');
      return response.data;
    } catch (error) {
      console.error('Error withdrawing all marketing consents:', error);
      throw error.response ? error.response.data : error;
    }
  },

  /**
   * Get compliance report
   * @returns {Object} Compliance report
   */
  getComplianceReport: async () => {
    try {
      const response = await apiClient.get('/user/consent/compliance-report');
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance report:', error);
      throw error.response ? error.response.data : error;
    }
  }
};

// Default export if needed, or just use named exports
export default {
  productApi,
  categoryApi,
  orderApi,
  configApi,
  discountApi,
  adminDiscountApi,
  popularProductsApi,
  recommendedProductsApi,
  adminPopularProductsApi,
  newAddedProductsApi,
  adminNewAddedProductsApi,
  discountedProductsApi,
  campaignApi,
  authApi,
  userApi,
  addressApi,
  favoritesApi,
  listsApi,
  cartApi,
  userCartApi,
  userOrdersApi,
  consentApi
};